import { Icon, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';

import {
  CONTACT_TYPE,
  FLOW_CODES,
  NON_EDITABLE_RECOGNITION_STATUS,
  NOTIFICATION_TYPE,
  PAYMENT_METHOD_TYPES,
  RECOGNITION_STATUSES,
  SEND_GIFT_ROUTE_PATHS,
  SOURCE_TYPES,
  dateLabelFormatDay
} from '@hero/constants';
import { CostSummary, StatusSummary, ToggleMenuGroup } from '@hero/molecules';
import {
  RecognitionDetailOverviewAccordion,
  RecognitionDetailShareMethodAccordion,
  RecognitionExperienceAccordion,
  RecognitionGiftsAccordion,
  RecipientsAccordion
} from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useDuplicateRecognition,
  useGetRecognition,
  useGetRecognitionAcceptanceRate,
  useGetRecognitionSpendingSummary,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useExpireRecognition } from '@hero/services/order-service/recognition/hooks/mutations/useExpireRecognition';
import { useNotification } from '@hero/store/hooks';
import { formatCurrency } from '@hero/utils';
import { getAverageTimeFromSeconds } from '@hero/utils/timeUtils';

import * as S from './style';

function OrderSummary({ setActiveTab }) {
  const { showNotification } = useNotification();

  const { recognitionCode } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showProductModal, setShowProductModal] = useState(false);

  const {
    data: recognition,
    refetch: refetchRecognition,
    isLoading: isLoadingRecognition
  } = useGetRecognition({ recognitionCode });

  const {
    data: recognitionSpendingSummary,
    isLoading: isLoadingSpendingSummary,
    isFetching: isFetchingSpendingSummary,
    isError: isErrorSpendingSummary,
    refetch: refetchSpendingSummary
  } = useGetRecognitionSpendingSummary({
    recognitionCode
  });

  const { mutateAsync: updateRecognition, isLoading: isUpdatingRecognition } =
    useUpdateRecognition();

  const { mutateAsync: expireRecognition, isLoading: isExpireLoading } = useExpireRecognition();

  const { data: recognitionAcceptanceRate } = useGetRecognitionAcceptanceRate({
    recognitionCode
  });
  const { mutateAsync: duplicateRecognition } = useDuplicateRecognition();

  const cardList = [
    {
      id: 'overview',
      icon: <Icon name="order" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('overview')}</Typography>,
      children: () => <RecognitionDetailOverviewAccordion recognition={recognition} />
    },
    {
      id: 'gifts',
      icon: <Icon name="gift-box" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('gifts')}</Typography>,
      children: () => (
        <RecognitionGiftsAccordion
          showProductModal={showProductModal}
          setShowProductModal={setShowProductModal}
        />
      )
    },
    {
      id: 'contacts',
      icon: <Icon name="contacts" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('recipients')}</Typography>,
      children: () => <RecipientsAccordion setActiveTab={setActiveTab} />
    },
    {
      id: 'experience',
      icon: <Icon name="smartphone" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('experience')}</Typography>,
      children: () => <RecognitionExperienceAccordion recognition={recognition} />
    }
  ];

  const isTeams = recognition?.source === SOURCE_TYPES.teams_app;

  if (!isTeams) {
    cardList.push({
      id: 'share',
      icon: <Icon name="upload" color="var(--color-accent-primary-default)" />,
      title: <Typography variant="body1Bold">{t('share')}</Typography>,
      isHided: NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
      children: () => (
        <RecognitionDetailShareMethodAccordion
          recognition={recognition}
          setActiveTab={setActiveTab}
        />
      )
    });
  }

  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  return (
    <S.PageContent>
      <ToggleMenuGroup
        multiMenuActive
        list={cardList.filter(card => card.id)}
        defaultActiveMenus={['overview']}
      />
      <S.RightContent>
        <CostSummary
          {...recognitionSpendingSummary}
          status={recognition?.status?.current}
          isLoadingFeature={isLoadingRecognition || isExpireLoading}
          isLoadingSpendingSummary={isLoadingSpendingSummary || isFetchingSpendingSummary}
          isError={isErrorSpendingSummary}
          featureType="recognition"
          feature={recognition}
          isCCPayment={isCCPayment}
          merchantOrder={recognition?.merchantOrder}
          onRefreshInvoice={refetchRecognition}
        />
        <StatusSummary
          isLoadingSpendingSummary={isLoadingSpendingSummary}
          isUpdatingRecognition={isUpdatingRecognition}
          duplicateText={recognition?.name}
          featureType="recognition"
          feature={recognition}
          spendingSummary={recognitionSpendingSummary}
          analytics={recognitionAcceptanceRate?.acceptanceRates}
          averageAcceptanceRate={getAverageTimeFromSeconds(
            recognitionAcceptanceRate?.avgAcceptanceTimeInSeconds
          )}
          isCCPayment={isCCPayment}
          isContactLess={recognition?.contacts?.type === CONTACT_TYPE.number}
          onArchive={() => {
            updateRecognition({
              params: { recognitionCode },
              body: {
                status: { current: RECOGNITION_STATUSES.archived }
              }
            });
          }}
          onExpire={async () => {
            await expireRecognition({
              code: recognition.code
            });

            await refetchRecognition();

            const spendingSummaryData = await refetchSpendingSummary();
            const estimatedRefundDate = dayjs(
              spendingSummaryData?.data?.refund?.estimatedAt
            ).format(dateLabelFormatDay);
            showNotification({
              type: NOTIFICATION_TYPE.toast,
              message: (
                <Trans
                  i18nKey={
                    dayjs().isAfter(dayjs(spendingSummaryData?.data?.refund?.estimatedAt))
                      ? 'remainingRefunded'
                      : 'remainingWillBeRefunded'
                  }
                  values={{
                    amount: formatCurrency(spendingSummaryData?.data?.refund?.total),
                    refundDate: estimatedRefundDate
                  }}
                  components={{ b: <b /> }}
                />
              )
            });

            navigate({
              pathname: ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognition.code),
              search: `?${createSearchParams({
                status: RECOGNITION_STATUSES.expired
              })}`
            });
          }}
          onReactivate={async ({ formValues }) => {
            await updateRecognition({
              params: { recognitionCode },
              body: {
                settings: {
                  expiresAt: formValues.expirationDate
                },
                status: { current: RECOGNITION_STATUSES.live }
              }
            });
            await refetchSpendingSummary();
            showNotification({
              type: NOTIFICATION_TYPE.toast,
              message: <Trans i18nKey="recognitionReactivated" components={{ b: <b /> }} />
            });

            navigate({
              pathname: ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognition.code),
              search: `?${createSearchParams({
                status: RECOGNITION_STATUSES.live
              })}`
            });
          }}
          onDuplicate={async formValues => {
            const response = await duplicateRecognition({
              code: recognition.code,
              body: formValues
            });

            if (recognition.flowCode === FLOW_CODES.simplified) {
              navigate(
                ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[recognition.meta.creationStep]](response.code)
              );
            }

            navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(response.code));
          }}
          giftCount={recognition?.products?.length}
          contactType={recognition?.contacts?.type}
          giftMessage={recognition?.experienceSettings?.messageParams?.message}
          setActiveTab={setActiveTab}
        />
      </S.RightContent>
    </S.PageContent>
  );
}

export { OrderSummary };
